import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  deserializarRango,
  serializarRango,
} from "../../hooks/filtrosPersistidos";
import {
  Table,
  Button,
  Select,
  Input,
  Tag,
  Tooltip,
  Card,
  Col,
  Divider,
  Row,
  Statistic,
  Typography,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowRightOutlined,
  DollarCircleOutlined,
  EditOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { isAdminRole } from "../../utils/roles.utils";
import dayjs from "dayjs";
import { Filtros } from "./filtros";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { estadisticasApi, type OcupacionKpi } from "../../services/estadisticas.api";
import { propiedadesApi, PropertyDto } from "../../services/propiedades.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { formatPhoneDisplay } from "../../utils/formatPhone";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import {
  ESTADIA_LABEL,
  ESTADIA_TOOLTIP,
  parseEstadiaQuery,
} from "../../utils/estadiaCode";
import { FiltroFecha } from "./filtroGlobalFecha";
import { exportToXlsxSingleSheet, fetchAllPages } from "./exportXls";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";
import RecalcBookingButton from "../../Components/RecalcBookingButton";
// CSS de la sección. Lo importamos acá (y no solo en finanzasEmpresa.tsx)
// porque /arriendos usa ArriendosSection sin pasar por esa página — sin
// este import, el override `.arriendos-sticky-table` no se cargaría en la
// ruta /arriendos y el header sticky no funcionaría ahí.
import "./finanzasEmpresa.css";
import { ahoraChile } from "../../utils/fechaChile";
import { paginacionServidor } from "../../utils/tablaPaginacion";

/** Filas por página al abrir la tabla de arriendos. */
const TAMANO_PAGINA_POR_DEFECTO = 20;

type JsonbItem = {
  name?: string;
  _f_val?: number;
};

type JsonbPair = { razon: string; monto: number };

function normalizeJsonb(val: JsonbItem[] | null | undefined): JsonbPair[] {
  if (!Array.isArray(val) || val.length === 0) return [];
  return val
    .map((it, idx) => ({
      razon: it.name ?? `Item ${idx + 1}`,
      monto: Number(it._f_val ?? 0),
    }))
    .filter((x) => Number.isFinite(x.monto));
}

export interface ArriendoRegistro {
  id: string;
  fechaCreacion?: string | null;
  idPropiedad?: number | null;
  nombrepropiedad?: string | null;
  /** Id del sector de la propiedad — resuelto backend-side vía join. */
  idSector?: number | null;
  /** Nombre del sector de la propiedad — resuelto backend-side vía join. */
  sector?: string | null;
  idPropiedadL?: number | null;
  idReservaL?: number | null;
  idReservaC?: number | null;
  nombrehuesped?: string | null;
  telefonohuesped?: string | null;
  numerohuespedes?: number | null;
  ingreso?: string | null; // DATEONLY -> ISO
  salida?: string | null; // DATEONLY -> ISO
  noches?: number | null;
  idplataforma?: number | null;
  nombreplataforma?: string | null;
  moneda?: string | null;
  cambiousdclp?: number | null;
  reservatotalclp?: number | null;
  precionochesusd?: number | null;
  precionochesclp?: number | null;
  limpiezaclp?: number | null;
  limpiezausd?: number | null;
  comisionplataformaclp?: number | null;
  comisionplataformausd?: number | null;
  extrasComisionClp: JsonbPair[] | null;
  extrasComisionUsd: JsonbPair[] | null;
  extrasSinComisionClp: JsonbPair[] | null;
  extrasSinComisionUsd: JsonbPair[] | null;
  serviciosextrasusd?: number | null;
  salidatardia?: boolean | null;
  extensionesnoches?: number | null;
  tasacomisionaf?: number | null;
  comisionafreserva?: number | null;
  comisionafextras?: number | null;
  ivareserva?: number | null;
  ivaextras?: number | null;
  reservatotalpropietario?: number | null;
  /**
   * Ganancia de Aloja Fácil Empresa por este arriendo. Ver doc
   * flujo-financiero.md §4.
   *
   * Fórmula: comisiones AF (noches + extras) + IVAs + otros cargos
   * netos − descuentos solo_af.
   *
   * No incluye transporte/materiales (viven en /finanzas-empresa →
   * Aseos) ni lavandería (vive en /finanzas-empresa → Lavandería).
   */
  ingresoafclp?: number | null;
  /** Subitem transporte TOTAL (con IVA). Snapshot de la propiedad. */
  preciotransporteneto?: number | null; // campo legacy — hoy mapea al total por compat
  /** Subitem materiales TOTAL (con IVA). Snapshot de la propiedad. */
  preciomaterialesneto?: number | null;
  ingresospropietarioextras?: number | null;
  action?: string | null;
  tipo?: string | null;
  inmutable?: boolean | null;
  /**
   * Si true, el Cliente ve este arriendo en /finanzas-cliente con los
   * montos. Si false, aparece como "En revisión". Se activa
   * automáticamente el 2do día hábil del mes por el cron, o manual por
   * admin. Ver docs/flujo-financiero.md §13.
   */
  visibleCliente?: boolean | null;
  notainterna?: number | null;
}
export interface OpcionBasica {
  id: number;
  nombre: string;
}

const currencyCLP = (v?: number | null) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
  }).format(n);
};

/**
 * Header con ícono (i) + tooltip explicativo. Textos canónicos vienen
 * del doc flujo-financiero.md §16. Preferir usar este helper en vez de
 * strings sueltos para que cualquier duda sobre qué significa un campo
 * quede resuelta desde el propio header.
 */
const TT = ({ label, help }: { label: string; help: React.ReactNode }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    <span>{label}</span>
    <Tooltip title={help}>
      <InfoCircleOutlined style={{ opacity: 0.6, fontSize: 12 }} />
    </Tooltip>
  </span>
);

type ExtraItem = { razon?: any; monto?: any } | null;

const ExtrasListCol: React.FC<{
  items?: ExtraItem[] | ExtraItem | null;
  field: "razon" | "monto";
}> = ({ items, field }) => {
  const safeItems = Array.isArray(items) ? items : items ? [items] : [];
  if (safeItems.length === 0) {
    return <div style={{ opacity: 0.6 }}>—</div>;
  }

  const toSafeText = (v: any) =>
    v === null || v === undefined
      ? "—"
      : typeof v === "object"
        ? JSON.stringify(v)
        : String(v);

  return (
    <div style={{ display: "grid", rowGap: 4 }}>
      {safeItems.map((it, i) =>
        field === "razon" ? (
          <div
            key={i}
            title={toSafeText(it?.razon)}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {toSafeText(it?.razon)}
          </div>
        ) : (
          <div key={i} style={{ textAlign: "center" }}>
            {currencyCLP(Number(it?.monto) || 0)}
          </div>
        ),
      )}
    </div>
  );
};

const visibleDefaults: (keyof ArriendoRegistro)[] = [
  // Código de estadía AF (booking.id) — identificador universal, primero.
  "id",
  "nombrepropiedad",
  "idReservaC",
  "nombrehuesped",
  "telefonohuesped",
  "ingreso",
  "salida",
  "nombreplataforma",
  "reservatotalclp",
  "precionochesclp",
  "limpiezaclp",
  "comisionplataformaclp",
  "extrasComisionClp",
  "salidatardia",
  "extensionesnoches",
  "tasacomisionaf",
  "comisionafreserva",
  "comisionafextras",
  "ivareserva",
  "ivaextras",
  "reservatotalpropietario",
  "ingresoafclp",
  "ingresospropietarioextras",
  "inmutable",
  "notainterna",
];

// ── KPI por sector — sub-componentes ─────────────────────────────────────
//
// Bloque de KPI cards que vive ARRIBA de la tabla en /finanzas-empresa →
// Arriendos. Una card por sector + una card-encabezado con los totales
// del período. La métrica principal es Σ Ganancia Aloja Fácil — todo
// resaltado en violeta porque es lo que finalmente entra a la caja AF.
// Las cards quedan ordenadas por Ganancia AF descendente.

interface SectorKpiAgg {
  sectorId: number | null;
  sectorNombre: string;
  esSinSector: boolean;
  count: number;
  ingresoAfTotal: number;
  reservaTotal: number;
  ingresoCliente: number;
  nochesTotal: number;
  precioNochesTotal: number;
  /**
   * Total de propiedades habilitadas en el sector (denominador de la
   * fracción "props arrendadas / disponibles"). Viene de
   * `propiedadesApi.getAll()` filtrando `habilitado=true`.
   */
  propsDisponibles: number;
  /**
   * Cantidad de propiedades distintas con al menos un arriendo en el
   * período (numerador). Se computa con un Set de `idPropiedad` durante
   * la agregación.
   */
  propsArrendadas: number;
  /**
   * Ocupación noche-por-noche del sector (P4, D2). Vienen del endpoint
   * `/estadisticas/ocupacion-kpis` (igual que el cron), agregadas por sector
   * sobre las propiedades habilitadas. La ocupación de días se calcula como
   * `nochesOcupadasKpi / nochesDisponiblesKpi` — razón agrupada, descuenta
   * bloqueos y recorta al borde del período. Reemplazan a la fórmula vieja
   * `Σ r.noches / (propsDisponibles × periodDays)` que no descontaba bloqueos.
   */
  nochesOcupadasKpi: number;
  nochesDisponiblesKpi: number;
}

interface TotalesAgg {
  count: number;
  ingresoAfTotal: number;
  reservaTotal: number;
  ingresoCliente: number;
  nochesTotal: number;
  precioNochesTotal: number;
  propsDisponibles: number;
  propsArrendadas: number;
  /** Ocupación noche-por-noche global (ver SectorKpiAgg). P4, D2. */
  nochesOcupadasKpi: number;
  nochesDisponiblesKpi: number;
}

/** Card individual de un sector. */
function SectorKpiCard({
  agg,
  share,
  onVerDetalle,
}: {
  agg: SectorKpiAgg;
  /** % de Ganancia AF que aporta este sector al total del período. */
  share: number;
  onVerDetalle: () => void;
}) {
  // Métricas derivadas. Protegemos contra div/0 cuando el sector tiene 0
  // arriendos o 0 noches (edge case raro pero defensivo igualmente).
  const avgEstadia = agg.count > 0 ? agg.nochesTotal / agg.count : 0;
  const avgPrecioNoche =
    agg.nochesTotal > 0 ? agg.precioNochesTotal / agg.nochesTotal : 0;
  const avgGananciaAf = agg.count > 0 ? agg.ingresoAfTotal / agg.count : 0;

  // Ocupación de propiedades: cuántas se arrendaron de las disponibles.
  // Si no hay propiedades en el catálogo del sector (caso del grupo "Sin
  // sector") el denominador queda en 0 — mostramos sólo el numerador.
  const propsPct =
    agg.propsDisponibles > 0
      ? (agg.propsArrendadas / agg.propsDisponibles) * 100
      : null;

  // Ocupación de días (P4, D2 — round 2026-06-20): noches efectivamente
  // ocupadas / noches disponibles, donde disponibles = días del período −
  // bloqueos del calendario. Calculado noche-por-noche en el backend (igual
  // que el cron): descuenta los bloqueos y recorta las reservas al borde del
  // período. Si el sector no tiene catálogo habilitado (p. ej. "Sin sector")
  // el denominador queda en 0 — mostramos solo el numerador.
  const daysOcupadas = agg.nochesOcupadasKpi;
  const daysDisponibles = agg.nochesDisponiblesKpi;
  const daysPct =
    daysDisponibles > 0 ? (daysOcupadas / daysDisponibles) * 100 : null;

  return (
    <Card
      style={{ height: "100%" }}
      bodyStyle={{ padding: "20px 24px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Tag color="purple" style={{ fontSize: 13, marginRight: 0 }}>
            <HomeOutlined /> {agg.sectorNombre}
          </Tag>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {agg.count} {agg.count === 1 ? "arriendo" : "arriendos"}
          </span>
          {share > 0 && (
            <Tag color="gold" style={{ fontSize: 11, marginLeft: "auto" }}>
              {share.toFixed(1)}% de la ganancia AF
            </Tag>
          )}
        </div>
      }
    >
      {/* Métrica principal: Ganancia AF (lo que entra a la caja AF Empresa). */}
      <div
        style={{
          padding: "12px 16px",
          background: "#f9f0ff",
          border: "2px solid #d3adf7",
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <Tooltip
          title={
            "Σ de la columna 'Ganancia Aloja Fácil' para todos los " +
            "arriendos del sector en el período. Es lo que finalmente " +
            "entra a la caja AF Empresa por estos arriendos: comisiones " +
            "AF (noches + extras) + IVAs + otros cargos netos − " +
            "descuentos 'solo AF'. NO incluye transporte/materiales " +
            "(viven en la pestaña Aseos) ni lavandería (vive en su " +
            "pestaña dedicada)."
          }
        >
          <div
            style={{
              fontSize: 12,
              color: "#722ed1",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            <DollarCircleOutlined /> Σ Ganancia Aloja Fácil{" "}
            <InfoCircleOutlined style={{ fontSize: 11 }} />
          </div>
        </Tooltip>
        <div style={{ fontSize: 26, fontWeight: 700, color: "#722ed1" }}>
          {currencyCLP(agg.ingresoAfTotal)}
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          Promedio por arriendo: <b>{currencyCLP(avgGananciaAf)}</b>
        </div>
      </div>

      {/* Ocupación: propiedades arrendadas y días ocupados sobre los
          disponibles. Dos métricas relevantes para entender qué tan
          aprovechado está el sector. */}
      <Row
        gutter={[12, 12]}
        style={{
          padding: "10px 12px",
          background: "#fafafa",
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <Col xs={12}>
          <Tooltip
            title={
              "Propiedades distintas que tuvieron al menos un arriendo en " +
              "el período, sobre el total de propiedades habilitadas del " +
              "sector. Ejemplo: 1/3 si en el sector hay 3 propiedades " +
              "habilitadas pero todos los arriendos del período cayeron " +
              "en una sola."
            }
          >
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>
              🏢 Props arrendadas <InfoCircleOutlined style={{ fontSize: 10 }} />
            </div>
          </Tooltip>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1f2937" }}>
            {agg.propsArrendadas}
            {agg.propsDisponibles > 0 ? (
              <>
                <span style={{ fontWeight: 400, color: "#888" }}>
                  {" "}
                  / {agg.propsDisponibles}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#52525b",
                    marginLeft: 6,
                  }}
                >
                  ({propsPct!.toFixed(0)}%)
                </span>
              </>
            ) : (
              <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                (sin catálogo)
              </span>
            )}
          </div>
        </Col>
        <Col xs={12}>
          <Tooltip
            title={
              "Ocupación real del sector: noches efectivamente ocupadas / " +
              "noches disponibles, donde disponibles = días del período − " +
              "bloqueos del calendario, sumado sobre las propiedades " +
              "habilitadas del sector. Se calcula noche por noche (igual que " +
              "el cierre mensual): descuenta los bloqueos y, si una reserva " +
              "cruza el borde del período, cuenta solo las noches dentro del " +
              "rango. Una noche reservada cuenta como ocupada aunque también " +
              "esté bloqueada. Es una razón agrupada (pondera por noches " +
              "disponibles), no el promedio de los % de cada propiedad. Ver " +
              "flujo-financiero.md §4.4."
            }
          >
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>
              📅 Días ocupados <InfoCircleOutlined style={{ fontSize: 10 }} />
            </div>
          </Tooltip>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1f2937" }}>
            {daysOcupadas}
            {daysDisponibles > 0 ? (
              <>
                <span style={{ fontWeight: 400, color: "#888" }}>
                  {" "}
                  / {daysDisponibles}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#52525b",
                    marginLeft: 6,
                  }}
                >
                  ({daysPct!.toFixed(1)}%)
                </span>
              </>
            ) : (
              <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                (sin catálogo)
              </span>
            )}
          </div>
        </Col>
      </Row>

      {/* Métricas secundarias: estadía, precio/noche, montos brutos. */}
      <Row gutter={[16, 12]}>
        <Col xs={12}>
          <Tooltip title="Promedio de noches por arriendo en el sector (estadía promedio).">
            <Statistic
              title={
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  Estadía promedio <InfoCircleOutlined style={{ fontSize: 10 }} />
                </span>
              }
              value={avgEstadia}
              precision={1}
              suffix={avgEstadia === 1 ? "noche" : "noches"}
              valueStyle={{ fontSize: 16, color: "#1f2937" }}
            />
          </Tooltip>
        </Col>
        <Col xs={12}>
          <Tooltip title="Precio promedio por noche cobrado al huésped: Σ costo noches / Σ noches. Refleja el ticket medio del sector.">
            <Statistic
              title={
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  Precio/noche prom. <InfoCircleOutlined style={{ fontSize: 10 }} />
                </span>
              }
              value={avgPrecioNoche}
              precision={0}
              formatter={(v) => currencyCLP(Number(v))}
              valueStyle={{ fontSize: 16, color: "#1f2937" }}
            />
          </Tooltip>
        </Col>
        <Col xs={12}>
          <Tooltip title="Σ Reserva total bruta cobrada al huésped en el sector (incluye noches + limpieza + extras).">
            <Statistic
              title={
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  Σ Reserva total <InfoCircleOutlined style={{ fontSize: 10 }} />
                </span>
              }
              value={agg.reservaTotal}
              precision={0}
              formatter={(v) => currencyCLP(Number(v))}
              valueStyle={{ fontSize: 14, color: "#52525b" }}
            />
          </Tooltip>
        </Col>
        <Col xs={12}>
          <Tooltip title="Σ Ingreso Cliente — lo que se liquida a los propietarios del sector tras descontar comisiones AF + plataforma + limpieza.">
            <Statistic
              title={
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  Σ Ingreso Cliente <InfoCircleOutlined style={{ fontSize: 10 }} />
                </span>
              }
              value={agg.ingresoCliente}
              precision={0}
              formatter={(v) => currencyCLP(Number(v))}
              valueStyle={{ fontSize: 14, color: "#52525b" }}
            />
          </Tooltip>
        </Col>
      </Row>

      {!agg.esSinSector && (
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <Button
            type="link"
            size="small"
            icon={<ArrowRightOutlined />}
            onClick={onVerDetalle}
          >
            Ver detalle del sector
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Bloque completo: card-encabezado de totales + grid de cards por sector. */
function KpiBySectorBlock({
  sectorAggregates,
  totales,
  loading,
  rangoLabel,
  onVerSector,
  onVerTodos,
}: {
  sectorAggregates: SectorKpiAgg[];
  totales: TotalesAgg;
  loading: boolean;
  rangoLabel: string;
  onVerSector: (sectorId: number | null) => void;
  /** Navega a /arriendos con el mes activo, sin filtro de sector (todos). */
  onVerTodos: () => void;
}) {
  const avgEstadiaGlobal =
    totales.count > 0 ? totales.nochesTotal / totales.count : 0;
  const avgPrecioNocheGlobal =
    totales.nochesTotal > 0 ? totales.precioNochesTotal / totales.nochesTotal : 0;
  const avgGananciaAfGlobal =
    totales.count > 0 ? totales.ingresoAfTotal / totales.count : 0;
  // Ocupación global del período (suma sobre TODAS las propiedades
  // habilitadas). propsDisponibles ya viene del catálogo habilitado completo.
  const propsPctGlobal =
    totales.propsDisponibles > 0
      ? (totales.propsArrendadas / totales.propsDisponibles) * 100
      : null;
  // Ocupación de días global (P4, D2): Σ noches ocupadas / Σ noches
  // disponibles (= días del período − bloqueos), noche-por-noche, igual que
  // el cron. Reemplaza Σ r.noches / (propsDisponibles × periodDays).
  const daysOcupadasGlobal = totales.nochesOcupadasKpi;
  const daysDisponiblesGlobal = totales.nochesDisponiblesKpi;
  const daysPctGlobal =
    daysDisponiblesGlobal > 0
      ? (daysOcupadasGlobal / daysDisponiblesGlobal) * 100
      : null;

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Badge del rango activo. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          padding: "8px 12px",
          background: "#f3e8ff",
          borderRadius: 6,
        }}
      >
        <span style={{ fontWeight: 600, color: "#7c3aed" }}>📅 Período:</span>
        <Tag color="purple" style={{ fontSize: 13, textTransform: "capitalize" }}>
          {rangoLabel}
        </Tag>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          (cambia el rango en el selector global de arriba)
        </span>
      </div>

      {/* Card de totales del período — Ganancia AF resaltada al máximo. */}
      <Card
        style={{
          marginBottom: 16,
          background: "linear-gradient(135deg, #f3e8ff 0%, #fafafa 100%)",
        }}
        bodyStyle={{ padding: "20px 24px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <Typography.Title level={5} style={{ margin: 0 }}>
            📊 Totales del período (todos los sectores)
          </Typography.Title>
          {/* Equivalente global de "Ver detalle del sector": abre /arriendos
              con el mes activo y TODOS los sectores. */}
          <Button
            type="primary"
            ghost
            size="small"
            icon={<ArrowRightOutlined />}
            onClick={onVerTodos}
          >
            Ver todos los arriendos del mes
          </Button>
        </div>

        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Tooltip title="Cantidad total de arriendos en el período (todos los sectores).">
              <Statistic
                title={
                  <span style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600 }}>
                    Nº total de arriendos <InfoCircleOutlined />
                  </span>
                }
                value={totales.count}
                valueStyle={{ color: "#7c3aed", fontSize: 24, fontWeight: 700 }}
              />
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Tooltip
              title={
                "Σ Ganancia Aloja Fácil — lo que finalmente entra a la caja " +
                "AF Empresa por estos arriendos. Es la métrica clave de " +
                "finanzas. Fórmula: comisiones AF (noches + extras) + IVAs + " +
                "otros cargos netos − descuentos 'solo AF'. NO incluye " +
                "transporte, materiales ni lavandería (esos viven en sus " +
                "propias pestañas)."
              }
            >
              <Statistic
                title={
                  <span style={{ fontSize: 13, color: "#722ed1", fontWeight: 600 }}>
                    <DollarCircleOutlined /> Σ Ganancia Aloja Fácil{" "}
                    <InfoCircleOutlined />
                  </span>
                }
                value={totales.ingresoAfTotal}
                precision={0}
                formatter={(v) => currencyCLP(Number(v))}
                valueStyle={{ color: "#722ed1", fontSize: 24, fontWeight: 700 }}
              />
            </Tooltip>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
              Promedio por arriendo: <b>{currencyCLP(avgGananciaAfGlobal)}</b>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Tooltip title="Σ Reserva total bruta cobrada al huésped en el período (incluye noches + limpieza + extras).">
              <Statistic
                title={
                  <span style={{ fontSize: 13, color: "#52525b", fontWeight: 600 }}>
                    Σ Reserva total <InfoCircleOutlined />
                  </span>
                }
                value={totales.reservaTotal}
                precision={0}
                formatter={(v) => currencyCLP(Number(v))}
                valueStyle={{ color: "#52525b", fontSize: 22, fontWeight: 700 }}
              />
            </Tooltip>
          </Col>
        </Row>

        <Divider style={{ margin: "16px 0 12px" }} />

        <Row gutter={[24, 12]}>
          <Col xs={24} sm={12} md={8}>
            <Tooltip title="Promedio de noches por arriendo en el período (estadía promedio global).">
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Estadía promedio <InfoCircleOutlined style={{ fontSize: 10 }} />
                  </span>
                }
                value={avgEstadiaGlobal}
                precision={1}
                suffix={avgEstadiaGlobal === 1 ? "noche" : "noches"}
                valueStyle={{ fontSize: 18, color: "#1f2937" }}
              />
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Tooltip title="Precio promedio por noche del período: Σ costo noches / Σ noches.">
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Precio/noche prom. <InfoCircleOutlined style={{ fontSize: 10 }} />
                  </span>
                }
                value={avgPrecioNocheGlobal}
                precision={0}
                formatter={(v) => currencyCLP(Number(v))}
                valueStyle={{ fontSize: 18, color: "#1f2937" }}
              />
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Tooltip title="Σ Ingreso Cliente del período — lo que se liquida a los propietarios.">
              <Statistic
                title={
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Σ Ingreso Cliente <InfoCircleOutlined style={{ fontSize: 10 }} />
                  </span>
                }
                value={totales.ingresoCliente}
                precision={0}
                formatter={(v) => currencyCLP(Number(v))}
                valueStyle={{ fontSize: 18, color: "#1f2937" }}
              />
            </Tooltip>
          </Col>
        </Row>

        {/* Ocupación global del período: cuántas props se usaron y qué
            tan ocupadas estuvieron en días. */}
        <Divider style={{ margin: "16px 0 12px" }} />
        <Row gutter={[24, 12]}>
          <Col xs={24} md={12}>
            <Tooltip
              title={
                "Propiedades distintas con al menos un arriendo en el " +
                "período sobre el total de propiedades habilitadas. " +
                "Indica qué fracción del portafolio AF tuvo actividad."
              }
            >
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 2 }}>
                🏢 Props arrendadas (global){" "}
                <InfoCircleOutlined style={{ fontSize: 10 }} />
              </div>
            </Tooltip>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1f2937" }}>
              {totales.propsArrendadas}
              {totales.propsDisponibles > 0 ? (
                <>
                  <span style={{ fontWeight: 400, color: "#888" }}>
                    {" "}
                    / {totales.propsDisponibles}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#52525b",
                      marginLeft: 8,
                    }}
                  >
                    ({propsPctGlobal!.toFixed(0)}%)
                  </span>
                </>
              ) : (
                <span style={{ fontSize: 12, color: "#888", marginLeft: 4 }}>
                  (sin catálogo)
                </span>
              )}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Tooltip
              title={
                "Ocupación real global: noches efectivamente ocupadas / " +
                "noches disponibles (= días del período − bloqueos del " +
                "calendario), sumado sobre todas las propiedades habilitadas. " +
                "Métrica clásica de ocupación hotelera, calculada noche por " +
                "noche igual que el cierre mensual: descuenta bloqueos y " +
                "recorta las reservas al borde del período. Es una razón " +
                "agrupada (pondera por noches disponibles), no el promedio de " +
                "los % por propiedad. Ver flujo-financiero.md §4.4."
              }
            >
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 2 }}>
                📅 Días ocupados (global){" "}
                <InfoCircleOutlined style={{ fontSize: 10 }} />
              </div>
            </Tooltip>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1f2937" }}>
              {daysOcupadasGlobal}
              {daysDisponiblesGlobal > 0 ? (
                <>
                  <span style={{ fontWeight: 400, color: "#888" }}>
                    {" "}
                    / {daysDisponiblesGlobal}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#52525b",
                      marginLeft: 8,
                    }}
                  >
                    ({daysPctGlobal!.toFixed(1)}%)
                  </span>
                </>
              ) : (
                <span style={{ fontSize: 12, color: "#888", marginLeft: 4 }}>
                  (sin catálogo)
                </span>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      {/* Grid de cards por sector. */}
      {loading ? (
        <Card style={{ marginBottom: 16, textAlign: "center", padding: 24 }}>
          <span style={{ color: "#888" }}>Cargando KPIs por sector…</span>
        </Card>
      ) : sectorAggregates.length === 0 ? (
        <Card style={{ marginBottom: 16, textAlign: "center", padding: 24 }}>
          <span style={{ color: "#888" }}>
            Sin arriendos en el período seleccionado.
          </span>
        </Card>
      ) : (
        <>
          <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
            🏘️ Detalle por sector
          </Typography.Title>
          <Row gutter={[16, 16]}>
            {sectorAggregates.map((agg) => {
              const share =
                totales.ingresoAfTotal > 0
                  ? (agg.ingresoAfTotal / totales.ingresoAfTotal) * 100
                  : 0;
              return (
                <Col
                  key={agg.sectorId ?? `sin-sector-${agg.sectorNombre}`}
                  xs={24}
                  md={12}
                  xl={8}
                >
                  <SectorKpiCard
                    agg={agg}
                    share={share}
                    onVerDetalle={() => onVerSector(agg.sectorId)}
                  />
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
}

/**
 * Claves del panel avanzado que son rangos de fecha (viajan como Dayjs dentro
 * del objeto de filtros). JSON las convierte en texto y al volver dejan de ser
 * Dayjs: el RangePicker revienta al pintarlas. Se guardan como "YYYY-MM-DD" y
 * se reconstruyen al leer. Ver docs/filtros-persistentes.md.
 */
const CLAVES_RANGO_FECHA = ["salida"];

/**
 * Qué se guarda del objeto de filtros. `bookingId` NO se guarda: es un salto
 * puntual (una alerta, "Ver en finanzas" del calendario), no una preferencia —
 * guardado, la próxima visita mostraría un solo arriendo sin nada en la URL
 * que lo explique.
 */
const serializarFiltrosArriendos = (valor: Record<string, any>) => {
  const { bookingId: _salto, ...resto } = valor ?? {};
  CLAVES_RANGO_FECHA.forEach((k) => {
    if (resto[k] !== undefined) resto[k] = serializarRango(resto[k] ?? null);
  });
  return resto;
};

const deserializarFiltrosArriendos = (
  crudo: unknown,
): Record<string, any> | undefined => {
  if (!crudo || typeof crudo !== "object" || Array.isArray(crudo))
    return undefined;
  const valor = { ...(crudo as Record<string, any>) };
  CLAVES_RANGO_FECHA.forEach((k) => {
    if (valor[k] === undefined) return;
    const rango = deserializarRango(valor[k]);
    // Un rango ilegible (guardado por una versión anterior) o a medias se
    // descarta solo: el resto de los filtros recordados sigue sirviendo.
    if (!rango || !rango[0] || !rango[1]) delete valor[k];
    else valor[k] = rango;
  });
  return valor;
};

export function ArriendosSection({
  propiedades,
  filtroFecha,
  clavePersistencia,
  refreshKey,
  enableRecalcAction,
  enableCalendarEditLink,
  userRole,
  onRecalculated,
  showKpiBySector,
}: {
  data: ArriendoRegistro[];
  loading?: boolean;
  propiedades: OpcionBasica[];
  filtroFecha?: FiltroFecha;
  /**
   * Prefijo con el que se recuerdan los filtros de ESTA tabla por usuario.
   * Lo pasa la página porque el mismo componente vive en /arriendos y en
   * /finanzas-empresa, y cada una tiene su propia búsqueda y sus filtros.
   */
  clavePersistencia: string;
  /** Contador de refresco manual (botón "Actualizar" del header). */
  refreshKey?: number;
  /**
   * Si es true, agrega una columna "Acciones" al final con un botón
   * de recálculo de finanzas por fila (`RecalcBookingButton`). Se activa
   * desde `/arriendos`. En `/finanzas-empresa` se deja en false (el
   * recálculo es una operación de gestión, no de auditoría).
   */
  enableRecalcAction?: boolean;
  /**
   * Si es true, agrega en la columna "Acciones" un enlace por fila que
   * abre el modal de EDICIÓN del arriendo en `/calendario` (deep-link
   * `?editBooking=<id>&propertyId=<idPropiedad>&from=<ingreso>&to=<salida>`).
   * Reutiliza el flujo de edición real del calendario (con sus checks de
   * solape y cascadas). Admin-only. Se activa desde `/arriendos`.
   */
  enableCalendarEditLink?: boolean;
  /** Rol del usuario actual — determina si el botón de recalc se muestra. */
  userRole?: string | string[] | null;
  /** Callback tras recalc exitoso — el caller puede invalidar caché o refetch. */
  onRecalculated?: () => void;
  /**
   * Si true, renderiza un bloque de KPIs por sector arriba de la tabla:
   * cantidad de arriendos, Σ Ganancia AF (lo que entra a AF Empresa),
   * promedio de noches por estadía, precio promedio por noche, etc.
   * Activado en /finanzas-empresa (vista de reporte). En /arriendos se
   * mantiene en false (esa vista es operativa, no de auditoría).
   */
  showKpiBySector?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Un enlace directo manda sobre los filtros recordados: si el admin llega
  // desde /alertas o desde "Ver en finanzas" del calendario tiene que ver ESE
  // arriendo, no el cruce con lo que dejó filtrado la semana pasada — dos
  // filtros en AND dejan la tabla vacía, y el que la vacía ni siquiera se ve.
  // Se lee en el primer render, que es cuando los hooks congelan `restaurar`.
  const hayEnlaceDirecto =
    searchParams.has("bookingId") ||
    searchParams.has("propiedadId") ||
    searchParams.has("sectorId");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  // Cuando `showKpiBySector` está activado, mantenemos un set separado de
  // TODAS las filas del período (no paginadas) para alimentar las KPI
  // cards por sector. La tabla principal sigue paginando de a 20 — no
  // mezclamos ambos flujos para no romper la paginación existente.
  const [allRowsForKpi, setAllRowsForKpi] = useState<ArriendoRegistro[]>([]);
  const [loadingKpi, setLoadingKpi] = useState(false);
  // Catálogo de propiedades habilitadas (para denominar ocupación por
  // sector). Sólo se carga cuando `showKpiBySector` está activado.
  // El catálogo cambia muy poco — lo fetcheamos una sola vez al montar.
  const [propsCatalog, setPropsCatalog] = useState<PropertyDto[]>([]);
  // KPIs de ocupación por propiedad (id → KPI) del período activo, calculados
  // noche-por-noche en el backend (igual que el cron). Alimentan la métrica
  // "Días ocupados" de las cards (P4, D2). Se refrescan al cambiar el período.
  const [ocupacionKpis, setOcupacionKpis] = useState<Map<number, OcupacionKpi>>(
    new Map(),
  );
  // Texto del buscador rápido. Se mapea al campo `_q` del backend
  // (búsqueda libre que el handler `arriendosEmpresa` resuelve con un OR
  // iLike sobre huésped / propiedad / IDs de reserva / teléfono /
  // plataforma / nota interna — ver `mapUiToApi` más abajo). Como el
  // listado se pagina server-side, no podemos filtrar client-side; el
  // buscador dispara un fetch nuevo con el query incluido.
  const [searchQuery, setSearchQuery] = useFiltroPersistido(
    `${clavePersistencia}.busqueda`,
    "",
    { validar: (v) => typeof v === "string", restaurar: !hayEnlaceDirecto },
  );
  const [filters, setFilters, setFiltersSinRecordar] = useFiltroPersistido<
    Record<string, any>
  >(
    `${clavePersistencia}.filtros`,
    {},
    {
      validar: (v) => !!v && typeof v === "object" && !Array.isArray(v),
      serializar: serializarFiltrosArriendos,
      deserializar: deserializarFiltrosArriendos,
      restaurar: !hayEnlaceDirecto,
    },
  );
  // La PÁGINA no se recuerda a propósito: volver a una tabla y aterrizar en
  // la página 7 desorienta más de lo que ayuda. El tamaño sí: es preferencia.
  const [page, setPage] = useState(1);
  const [tamanoPagina, setTamanoPagina] = useFiltroPersistido(
    `${clavePersistencia}.filas`,
    TAMANO_PAGINA_POR_DEFECTO,
    { validar: (v) => typeof v === "number" && v > 0 && v <= 200 },
  );
  const [total, setTotal] = useState(0);
  // Catálogo de sectores para el Select "Sector" del toolbar de /arriendos.
  // Solo se carga en modo operativo (no en la vista de reporte con KPI cards).
  const [sectorOpts, setSectorOpts] = useState<{ id: number; sector: string }[]>(
    [],
  );
  // Column visibility state (persist in localStorage)
  // El setter quedó sin uso al eliminar setVisible (Fase 2.5 cleanup).
  const [visibleKeys] = useState<(keyof ArriendoRegistro)[]>(
    () => {
      const k = localStorage.getItem("arrColVisible");
      if (!k) return visibleDefaults;
      try {
        const stored = JSON.parse(k) as (keyof ArriendoRegistro)[];
        if (!Array.isArray(stored)) return visibleDefaults;
        // Migración (round 2026-07-16): la columna "id" (Código de estadía AF)
        // pasó a ser visible por defecto. Si un valor viejo de localStorage no
        // la incluye, la inyectamos al frente SIN perder el resto de las
        // preferencias del admin (en vez de bumpear la clave y resetear todo).
        return stored.includes("id") ? stored : ["id", ...stored];
      } catch {
        return visibleDefaults;
      }
    },
  );
  const colDefs: Record<keyof ArriendoRegistro, any> = {
    id: {
      // Código de estadía AF (booking.id). NO confundir con "ID Reserva C."
      // (id_reservac, código de plataforma). El tooltip explicativo va en el
      // header (una vez); las celdas usan el Tag sin tooltip.
      title: (
        <Tooltip title={ESTADIA_TOOLTIP}>
          <span>{ESTADIA_LABEL}</span>
        </Tooltip>
      ),
      dataIndex: "id",
      key: "id",
      width: 160,
      align: "center",
      sorter: (a: any, b: any) => Number(a.id ?? 0) - Number(b.id ?? 0),
      render: (v: any) => <EstadiaCodeTag id={v} withTooltip={false} />,
    },
    fechaCreacion: {
      title: "Creación",
      dataIndex: "fechaCreacion",
      key: "fechaCreacion",
      width: 140,
      align: "center",
      render: (v: string) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
      sorter: (a: any, b: any) =>
        dayjs(a.fechaCreacion).unix() - dayjs(b.fechaCreacion).unix(),
    },
    idPropiedad: {
      title: "ID Propiedad",
      dataIndex: "idPropiedad",
      key: "idPropiedad",
      width: 120,
      align: "center",
    },
    nombrepropiedad: {
      title: "Propiedad",
      dataIndex: "nombrepropiedad",
      key: "nombrepropiedad",
      width: 220,
      ellipsis: true,
      align: "center",
      // Presentación: nombre de propiedad SIEMPRE en mayúsculas (solo
      // display, el dato crudo se conserva para sort/filtro). El sorter
      // sigue ordenando sobre el valor original.
      render: (v?: string | null) => formatPropertyName(v) || "—",
      // Defensivo: nombrepropiedad puede venir null/undefined en filas
      // legacy o en reservas con propiedad eliminada. Llamar
      // `null.localeCompare(...)` revienta con TypeError, y como esta
      // sección NO está envuelta en SectionErrorBoundary, el crash
      // desmonta toda la página de Finanzas Empresa → "pantalla blanca"
      // al hacer clic en el header para ordenar alfabéticamente.
      // Mismo patrón aplicado a todas las columnas string-sorter de
      // abajo (idPropiedadL, idReservaL, idReservaC, nombrehuesped,
      // idplataforma, nombreplataforma, moneda).
      sorter: (a: any, b: any) =>
        (a.nombrepropiedad ?? "").localeCompare(b.nombrepropiedad ?? ""),
    },
    idPropiedadL: {
      title: "ID Prop L.",
      dataIndex: "idPropiedadL",
      key: "idPropiedadL",
      width: 120,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.idPropiedadL ?? "").localeCompare(b.idPropiedadL ?? ""),
    },
    idReservaL: {
      title: "ID Reserva L.",
      dataIndex: "idReservaL",
      key: "idReservaL",
      width: 140,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.idReservaL ?? "").localeCompare(b.idReservaL ?? ""),
    },
    idReservaC: {
      title: "ID Reserva C.",
      dataIndex: "idReservaC",
      key: "idReservaC",
      width: 140,
      ellipsis: true,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.idReservaC ?? "").localeCompare(b.idReservaC ?? ""),
    },
    nombrehuesped: {
      title: "Huésped",
      dataIndex: "nombrehuesped",
      key: "nombrehuesped",
      width: 200,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.nombrehuesped ?? "").localeCompare(b.nombrehuesped ?? ""),
    },
    telefonohuesped: {
      title: "Teléfono",
      dataIndex: "telefonohuesped",
      key: "telefonohuesped",
      width: 150,
      align: "center",
      render: (v: any) => formatPhoneDisplay(v),
    },
    numerohuespedes: {
      title: "#Huéspedes",
      dataIndex: "numerohuespedes",
      key: "numerohuespedes",
      width: 120,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.numerohuespedes || 0) - (b.numerohuespedes || 0),
    },
    ingreso: {
      title: "Entrada",
      dataIndex: "ingreso",
      key: "ingreso",
      width: 130,
      align: "center",
      render: (v: string) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
      sorter: (a: any, b: any) =>
        dayjs(a.ingreso).unix() - dayjs(b.ingreso).unix(),
    },
    salida: {
      title: "Salida",
      dataIndex: "salida",
      key: "salida",
      width: 130,
      align: "center",
      render: (v: string) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
      sorter: (a: any, b: any) =>
        dayjs(a.salida).unix() - dayjs(b.salida).unix(),
    },
    noches: {
      title: "Noches",
      dataIndex: "noches",
      key: "noches",
      width: 100,
      align: "center",
      sorter: (a: any, b: any) => (a.noches || 0) - (b.noches || 0),
    },
    idplataforma: {
      title: "ID Plataforma",
      dataIndex: "idplataforma",
      key: "idplataforma",
      width: 130,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.idplataforma ?? "").localeCompare(b.idplataforma ?? ""),
    },
    nombreplataforma: {
      title: "Plataforma",
      dataIndex: "nombreplataforma",
      key: "nombreplataforma",
      width: 150,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.nombreplataforma ?? "").localeCompare(b.nombreplataforma ?? ""),
      render: (plataforma: string) => {
        let label: string;
        let tagColor: string;

        switch (plataforma) {
          case "AirBnb":
            label = "AirBnb";
            tagColor = "red";
            break;
          case "Booking":
            label = "Booking";
            tagColor = "blue";
            break;
          case "Stays":
            // "Stays" (DB) == reserva entrada por canales integrados
            // (sitio web / Stays.net). Se muestra como "Web".
            label = "Web";
            tagColor = "green";
            break;
          case "Directa":
            // "Directa" == reserva creada manualmente por admin.
            label = "Directo";
            tagColor = "blue";
            break;
          default:
            label = plataforma;
            tagColor = "purple";
            break;
        }

        return <Tag color={tagColor}>{label}</Tag>;
      },
    },
    moneda: {
      title: "Moneda",
      dataIndex: "moneda",
      key: "moneda",
      width: 110,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.moneda ?? "").localeCompare(b.moneda ?? ""),
    },
    cambiousdclp: {
      title: "Cambio USD/CLP",
      dataIndex: "cambiousdclp",
      key: "cambiousdclp",
      width: 150,
      align: "center",
    },
    reservatotalclp: {
      title: (
        <TT
          label="Reserva total (CLP)"
          help="Todo lo que paga el huésped por este arriendo. Fórmula: precio noches efectivo + limpieza + extras con y sin comisión. Doc §3."
        />
      ) as any,
      dataIndex: "reservatotalclp",
      key: "reservatotalclp",
      width: 170,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.reservatotalclp || 0) - (b.reservatotalclp || 0),
      render: currencyCLP,
    },
    precionochesclp: {
      title: (
        <TT
          label="Costo total de las noches (CLP)"
          help="Total cobrado al huésped por las noches del arriendo: base × noches + extensiones + salida tardía, menos descuentos tipo 'ajuste al total'. Es la base del cálculo de comisión AF sobre noches."
        />
      ) as any,
      dataIndex: "precionochesclp",
      key: "precionochesclp",
      width: 180,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.precionochesclp || 0) - (b.precionochesclp || 0),
      render: currencyCLP,
    },
    precionochesusd: {
      title: "Costo total de las noches (USD)",
      dataIndex: "precionochesusd",
      key: "precionochesusd",
      width: 160,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.precionochesusd || 0) - (b.precionochesusd || 0),
    },
    limpiezaclp: {
      title: (
        <TT
          label="Limpieza (CLP)"
          help={`Total de limpieza que paga el huésped, repartido en 4 subitems:

• Pago aseador → va al aseador.
• Transporte y materiales → entran a /finanzas-empresa → Aseos.
• Lavandería → entra a /finanzas-empresa → Lavandería.

El Cliente no recibe nada de la limpieza. Estos subitems NO se suman a 'Ganancia Aloja Fácil' del arriendo — viven en sus pestañas dedicadas.`}
        />
      ) as any,
      dataIndex: "limpiezaclp",
      key: "limpiezaclp",
      width: 150,
      align: "center",
      sorter: (a: any, b: any) => (a.limpiezaclp || 0) - (b.limpiezaclp || 0),
      render: currencyCLP,
    },
    limpiezausd: {
      title: "Limpieza (USD)",
      dataIndex: "limpiezausd",
      key: "limpiezausd",
      width: 140,
      align: "center",
      sorter: (a: any, b: any) => (a.limpiezausd || 0) - (b.limpiezausd || 0),
    },
    comisionplataformaclp: {
      title: (
        <TT
          label="Comisión plataforma (CLP)"
          help="Lo que cobra Airbnb / Booking / Despegar por gestionar el arriendo. Este valor YA INCLUYE IVA — no se suma 19% adicional. Si la reserva es directa (sin plataforma), suele ser 0."
        />
      ) as any,
      dataIndex: "comisionplataformaclp",
      key: "comisionplataformaclp",
      width: 200,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.comisionplataformaclp || 0) - (b.comisionplataformaclp || 0),
      render: currencyCLP,
    },
    comisionplataformausd: {
      title: "Comisión plataforma (USD)",
      dataIndex: "comisionplataformausd",
      key: "comisionplataformausd",
      width: 200,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.comisionplataformausd || 0) - (b.comisionplataformausd || 0),
    },
    extrasComisionClp: {
      title: "Extras (CLP)",
      dataIndex: "extrasComisionClp",
      key: "extrasComisionClp",
      width: 140,
      align: "center",
      render: currencyCLP,
      sorter: (a: any, b: any) =>
        (a.extrascomisionclp || 0) - (b.extrascomisionclp || 0),
    },
    serviciosextrasusd: {
      title: "Extras (USD)",
      dataIndex: "serviciosextrasusd",
      key: "serviciosextrasusd",
      width: 140,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.serviciosextrasusd || 0) - (b.serviciosextrasusd || 0),
    },
    salidatardia: {
      title: "Salida tardía",
      dataIndex: "salidatardia",
      key: "salidatardia",
      width: 130,
      align: "center",
      render: (v: boolean) => (v ? "Sí" : "No"),
      sorter: (a: any, b: any) => {
        if (a.salidatardia === b.salidatardia) return 0;
        return a.salidatardia ? 1 : -1;
      },
    },
    extensionesnoches: {
      title: "Extensiones (noches)",
      dataIndex: "extensionesnoches",
      key: "extensionesnoches",
      width: 170,
      align: "center",
    },
    tasacomisionaf: {
      title: "Tasa comisión AF",
      dataIndex: "tasacomisionaf",
      key: "tasacomisionaf",
      width: 160,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.tasacomisionaf || 0) - (b.tasacomisionaf || 0),
    },
    comisionafreserva: {
      title: (
        <TT
          label="Comisión AF reserva"
          help="Comisión de Aloja Fácil sobre el precio efectivo de noches (post ajuste al total). Fórmula: precioNochesEfectivo × tasaComisionAf. Suma al ingreso AF Empresa."
        />
      ) as any,
      dataIndex: "comisionafreserva",
      key: "comisionafreserva",
      width: 180,
      align: "center",
      render: currencyCLP,
      sorter: (a: any, b: any) =>
        (a.comisionafreserva || 0) - (b.comisionafreserva || 0),
    },
    comisionafextras: {
      title: (
        <TT
          label="Comisión AF extras"
          help="Comisión de Aloja Fácil sobre los extras CON comisión (mascotas, personas adicionales, etc.). Los 'extras sin comisión' NO generan comisión. Fórmula: sum(extras con comisión) × tasaComisionAf."
        />
      ) as any,
      dataIndex: "comisionafextras",
      key: "comisionafextras",
      width: 170,
      align: "center",
      render: currencyCLP,
      sorter: (a: any, b: any) =>
        (a.comisionafextras || 0) - (b.comisionafextras || 0),
    },
    ivareserva: {
      title: (
        <TT
          label="IVA reserva"
          help="IVA 19% sobre la comisión AF reserva. Entra como caja de Aloja Fácil Empresa. Se compensa manualmente con el IVA crédito en la liquidación SII mensual (no automatizado — doc §8)."
        />
      ) as any,
      dataIndex: "ivareserva",
      key: "ivareserva",
      width: 140,
      align: "center",
      render: currencyCLP,
      sorter: (a: any, b: any) => (a.ivareserva || 0) - (b.ivareserva || 0),
    },
    ivaextras: {
      title: (
        <TT
          label="IVA extras"
          help="IVA 19% sobre la comisión AF extras. Entra como caja de Aloja Fácil Empresa. Se compensa manual al SII (doc §8)."
        />
      ) as any,
      dataIndex: "ivaextras",
      key: "ivaextras",
      width: 140,
      align: "center",
      render: currencyCLP,
      sorter: (a: any, b: any) => (a.ivaextras || 0) - (b.ivaextras || 0),
    },
    reservatotalpropietario: {
      title: (
        <TT
          label="Ingreso Cliente"
          help={`Lo que le queda al Cliente (propietario) por este arriendo.

Fórmula (doc §5):
Reserva total
− Comisión plataforma (ya con IVA)
− Comisión AF noches + IVA
− Comisión AF extras + IVA
− Limpieza entera

Los extras sin comisión van 100% al cliente.
Los descuentos 'solo AF' NO afectan al cliente.`}
        />
      ) as any,
      dataIndex: "reservatotalpropietario",
      key: "reservatotalpropietario",
      width: 170,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.reservatotalpropietario || 0) - (b.reservatotalpropietario || 0),
      render: currencyCLP,
    },
    ingresoafclp: {
      // Ganancia AF por arriendo. Solo comisiones + otros − descSoloAf.
      // Los subitems del aseo y la lavandería no entran — viven en sus
      // pestañas dedicadas (Aseos y Lavandería) para evitar doble conteo.
      title: (
        <TT
          label="Ganancia Aloja Fácil"
          help={`Lo que gana AF Empresa por este arriendo:

  Comisión AF noches + IVA
+ Comisión AF extras + IVA
+ Otros cargos 'recibe' − 'paga'
− Descuentos 'solo AF'

NO incluye:
• Pago al aseador (va al aseador).
• Transporte y materiales (van a /finanzas-empresa → Aseos).
• Lavandería (va a /finanzas-empresa → Lavandería).

Así cada peso vive en una sola pestaña, sin doble conteo.`}
        />
      ) as any,
      dataIndex: "ingresoafclp",
      key: "ingresoafclp",
      width: 180,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.ingresoafclp || 0) - (b.ingresoafclp || 0),
      render: currencyCLP,
    },
    ingresospropietarioextras: {
      title: "Ingresos prop. extras",
      dataIndex: "ingresospropietarioextras",
      key: "ingresospropietarioextras",
      width: 190,
      align: "center",
      sorter: (a: any, b: any) =>
        (a.ingresospropietarioextras || 0) - (b.ingresospropietarioextras || 0),
      render: currencyCLP,
    },
    // NOTA: la antigua columna "Visible cliente" (toggle per-arriendo
    // visible_cliente) se removió en el round 2026-06-16. La visibilidad al
    // cliente ahora la decide el CIERRE MENSUAL unificado por (propiedad, mes),
    // no un flag por arriendo. Publicar/ocultar se hace con el botón
    // "Cerrar / publicar mes al cliente…" (modal de cierre). Ver §13.
    action: {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 120,
      align: "center",
    },
    tipo: {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      width: 120,
      align: "center",
    },
    inmutable: {
      title: "Inmutable",
      dataIndex: "inmutable",
      key: "inmutable",
      width: 120,
      align: "center",
      render: (v: boolean) => (v ? "Sí" : "No"),
    },
    notainterna: {
      title: "Nota interna",
      dataIndex: "notainterna",
      key: "notainterna",
      width: 130,
      align: "center",
    },
  } as any;

  const buildColumns = (): ColumnsType<ArriendoRegistro> => {
    const order: (keyof ArriendoRegistro)[] = [
      "id",
      "nombrepropiedad",
      "idReservaC",
      "nombrehuesped",
      "telefonohuesped",
      "ingreso",
      "salida",
      "nombreplataforma",
      "reservatotalclp",
      "precionochesclp",
      "limpiezaclp",
      "comisionplataformaclp",
      "extrasComisionClp",
      "salidatardia",
      "extensionesnoches",
      "tasacomisionaf",
      "comisionafreserva",
      "comisionafextras",
      "ivareserva",
      "ivaextras",
      "reservatotalpropietario",
      "ingresoafclp",
      "ingresospropietarioextras",
      "inmutable",
      "notainterna",
      // resto
      "fechaCreacion",
      "idPropiedad",
      "idPropiedadL",
      "idReservaL",
      "numerohuespedes",
      "noches",
      "idplataforma",
      "moneda",
      "cambiousdclp",
      "precionochesusd",
      "limpiezausd",
      "comisionplataformausd",
      "extrasSinComisionClp",
      "extrasSinComisionUsd",
      "extrasComisionUsd",
      "action",
      "tipo",
    ];

    const base = order
      .filter((k) => visibleKeys.includes(k))
      .map((k) => colDefs[k]);

    const cols: ColumnsType<ArriendoRegistro> = [];
    for (const col of base) {
      if (col?.key === "extrasComisionClp") {
        cols.push({
          key: "extrasGroup",
          title: "Extras con comisión (CLP)",
          align: "center",
          children: [
            {
              key: "extras_razon",
              title: "Razón",
              align: "center",
              width: 220,
              render: (_: any, r: ArriendoRegistro) => (
                <ExtrasListCol items={r.extrasComisionClp} field="razon" />
              ),
            },
            {
              key: "extras_monto",
              title: "Monto",
              align: "center",
              width: 160,
              render: (_: any, r: ArriendoRegistro) => (
                <ExtrasListCol items={r.extrasComisionClp} field="monto" />
              ),
            },
          ],
        });
      } else {
        cols.push(col);
      }
    }
    // Columna "Acciones" — solo se monta si el caller pide alguna acción
    // por fila (`enableRecalcAction` = recálculo de finanzas; y/o
    // `enableCalendarEditLink` = abrir el arriendo en el modal de edición
    // del calendario). Ambos flags se activan desde `/arriendos`; en
    // `/finanzas-empresa` (auditoría) van en false. Los controles filtran
    // por rol adentro (admin-only). Se agrega al FINAL para no romper el
    // orden de columnas que el usuario tenga persistido en localStorage.
    if (enableRecalcAction || enableCalendarEditLink) {
      const canEditInCalendar = enableCalendarEditLink && isAdminRole(userRole);
      cols.push({
        key: "_acciones",
        // Header con (i) explicando para qué sirve la columna. El detalle
        // completo lo da el modal de confirmación al click; el tooltip
        // del header sirve para que el admin entienda la columna sin
        // tener que apretar el botón.
        title: (
          <TT
            label="Acciones"
            help={
              <>
                {canEditInCalendar && (
                  <div style={{ marginBottom: 6 }}>
                    <strong>Editar en el calendario:</strong> abre este
                    arriendo en el modal de edición del calendario (mismo
                    formulario, con validaciones de solape y cascadas de
                    aseo/bloqueo).
                  </div>
                )}
                {enableRecalcAction && (
                  <div>
                    <strong>Recalcular finanzas:</strong> reprocesa el último
                    JSON de Stays para esa reserva y actualiza sólo los campos
                    financieros (precio noches, comisiones, IVA, ingreso AF,
                    ingreso propietario). Útil cuando cambian los subitems de
                    la propiedad o el algoritmo. No toca fechas, huésped,
                    propiedad ni satélites.
                  </div>
                )}
              </>
            }
          />
        ),
        align: "center",
        width: canEditInCalendar && enableRecalcAction ? 120 : 100,
        fixed: "right",
        render: (_: any, r: ArriendoRegistro) => {
          const idNum = Number(r.id);
          if (!Number.isFinite(idNum) || idNum <= 0) return null;
          // Enlace de edición: solo si tenemos propiedad + rango de fechas
          // (los necesita el deep-link para cargar el booking en la data
          // del calendario y abrir el modal). Un arriendo sin propiedad o
          // sin fechas no es editable desde acá.
          const canLinkRow =
            canEditInCalendar &&
            r.idPropiedad != null &&
            !!r.ingreso &&
            !!r.salida;
          return (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                justifyContent: "center",
              }}
            >
              {canLinkRow && (
                <Tooltip title="Editar en el calendario">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    aria-label="Editar en el calendario"
                    onClick={() => {
                      const params = new URLSearchParams({
                        editBooking: String(idNum),
                        propertyId: String(r.idPropiedad),
                        from: String(r.ingreso),
                        to: String(r.salida),
                      });
                      navigate(`/calendario?${params.toString()}`);
                    }}
                  />
                </Tooltip>
              )}
              {enableRecalcAction && (
                <RecalcBookingButton
                  bookingId={idNum}
                  userRole={userRole}
                  onRecalculated={onRecalculated}
                  tasaComisionHistoricaPct={r.tasacomisionaf ?? null}
                  variant="icon-only"
                  size="small"
                />
              )}
            </div>
          );
        },
      });
    }
    return cols;
  };

  // El antiguo `items` (Dropdown menu de columnas client-side) y
  // `filteredData` (filtrado client-side por dayjs/contains) se eliminaron:
  // el filtrado vive ahora en backend vía `mapUiToApi` y la elección de
  // columnas usa el `Settings` Popover renderizado más abajo. Si vuelve a
  // hacer falta lógica client-side, recuperar de git.

  const mapUiToApi = (
    uiFilters: Record<string, any>,
    page: number,
    pageSize: number = TAMANO_PAGINA_POR_DEFECTO,
  ) => {
    const fields: any = {};

    if (uiFilters._q) fields._q = uiFilters._q;

    // Texto: UI key -> DB field (Sequelize)
    const contains = (uiKey: string, dbField: string) => {
      const val = uiFilters[uiKey];
      if (val != null && val !== "") {
        fields[dbField] = { contains: val };
      }
    };

    contains("idReservaC", "id_reservac");
    contains("nombrePropiedad", "nombrepropiedad");
    contains("nombreHuesped", "nombre_huesped");
    // Teléfono: la DB lo guarda "pegado" (solo dígitos) pero la tabla lo
    // muestra agrupado de a 4 — si el admin copia/tipea el formato visible,
    // el iLike no matcheaba. Comparamos solo dígitos (auditoría 2026-07-13).
    if (uiFilters.telefonoHuesped != null && uiFilters.telefonoHuesped !== "") {
      const soloDigitos = String(uiFilters.telefonoHuesped).replace(
        /[^\d+]/g,
        "",
      );
      if (soloDigitos) fields.telefono_huesped = { contains: soloDigitos };
    }
    // Plataforma es Select múltiple: mandar `in` con los valores exactos.
    // Antes iba como `contains` de texto y con 2+ seleccionadas el backend
    // buscaba iLike '%AirBnb,Booking%' → 0 filas (auditoría 2026-07-13).
    if (
      Array.isArray(uiFilters.nombrePlataforma) &&
      uiFilters.nombrePlataforma.length > 0
    ) {
      fields.nombre_plataforma = { in: uiFilters.nombrePlataforma };
    } else if (
      uiFilters.nombrePlataforma != null &&
      uiFilters.nombrePlataforma !== "" &&
      !Array.isArray(uiFilters.nombrePlataforma)
    ) {
      fields.nombre_plataforma = { contains: uiFilters.nombrePlataforma };
    }
    contains("notaInterna", "notainterna");

    // Fechas (rangos)
    const range = (uiKey: string, dbField: string) => {
      const v = uiFilters[uiKey];
      if (Array.isArray(v) && v.length === 2) {
        fields[dbField] = {
          from: dayjs(v[0]).format("YYYY-MM-DD"),
          to: dayjs(v[1]).format("YYYY-MM-DD"),
        };
      }
    };

    range("ingreso", "ingreso");
    range("salida", "salida");
    range("creacionRango", "fecha_creacion");

    // Rangos numéricos — el contrato del backend es `{ eq: {min,max} }`
    // (buildWhereFromFields lo convierte a between/gte/lte). Antes se mandaba
    // el objeto {min,max} pelado, que no cae en ninguna rama y se descartaba
    // en silencio: ningún rango filtraba (auditoría 2026-07-13).
    const numRange = (uiKey: string, dbField: string) => {
      const v = uiFilters[uiKey];
      if (v && (v.min != null || v.max != null)) {
        fields[dbField] = { eq: v };
      }
    };

    numRange("reservaTotalClp", "reserva_total_clp");
    numRange("limpiezaClp", "limpieza_clp");
    numRange("comisionPlataformaClp", "comision_plataforma_clp");
    numRange("reservaTotalPropietario", "reserva_total_propietario");
    // extras_comision_clp es JSONB (lista de extras), no una columna numérica
    // — su numberRange se retiró del panel (era un filtro muerto).

    // Propiedad es Select múltiple: `in` con los ids. Antes `{eq: array}` →
    // Number([5,7]) = NaN con 2+ seleccionadas (auditoría 2026-07-13).
    if (Array.isArray(uiFilters.propiedadId)) {
      if (uiFilters.propiedadId.length > 0) {
        fields.id_propiedad = { in: uiFilters.propiedadId };
      }
    } else if (uiFilters.propiedadId != null) {
      fields.id_propiedad = { eq: uiFilters.propiedadId };
    }

    // Sector (Select "Sector" de /arriendos + deep-link ?sectorId): campo
    // virtual `_idSector` que el backend resuelve a las propiedades del sector
    // (el booking no tiene columna de sector — vive en la propiedad).
    if (uiFilters.idSector != null && uiFilters.idSector !== "") {
      fields._idSector = uiFilters.idSector;
    }

    // Filtro "Código de estadía AF" (#N) del panel avanzado: el usuario escribe
    // "#1472" o "142" y filtra por SUBSTRING del código (parcial): "142"
    // encuentra 142, 1424, 1420, etc. (pedido del owner 2026-07-16). Se manda
    // como campo virtual `_codigoEstadia` y el backend lo resuelve con un
    // `CAST(id AS TEXT) LIKE %cod%` (arriendosEmpresa). NO usa `fields.id={eq}`
    // porque ése hacía match exacto vía buildWhereFromFields.
    const cod = parseEstadiaQuery(uiFilters.codigoEstadia ?? "");
    if (cod) fields._codigoEstadia = cod;

    // Prefiltro por arriendo puntual (botón "Ver en finanzas" del
    // calendario → /arriendos?bookingId=123). `id` es la PK del Booking,
    // así la tabla muestra SOLO ese arriendo. Va DESPUÉS de codigoEstadia para
    // que el deep-link puntual gane si por algún motivo ambos vinieran.
    if (uiFilters.bookingId != null) {
      fields.id = { eq: uiFilters.bookingId };
    }

    // Salida tardía vive DENTRO del JSONB `adicionales_arriendo`
    // (key `salida_tardia`), no como columna. Se manda como campo virtual
    // `_salidaTardia` y el backend lo resuelve con un literal JSONB
    // (arriendosEmpresa). Antes este mapeo estaba comentado y el filtro
    // era un no-op silencioso (auditoría 2026-07-13).
    if (uiFilters.salidaTardia != null) {
      fields._salidaTardia = uiFilters.salidaTardia === true;
    }

    return { filters: { fields }, page, page_size: pageSize };
  };

  const buildGlobalRange = () => {
    const ff = filtroFecha ?? { modo: "mes", mes: ahoraChile() };

    // "Todos": sin ventana de fecha → el backend no aplica filtro por fecha
    // (globalDateRange queda undefined). Muestra TODOS los arriendos del
    // histórico, sin importar el mes. Solo se ofrece en /arriendos.
    if (ff.modo === "todos") return null;

    if (ff.modo === "mes" || ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile();
      return { from: m.startOf("month"), to: m.endOf("month") };
    }
    if (ff.modo === "rango" && ff.rango) {
      return { from: ff.rango[0].startOf("day"), to: ff.rango[1].endOf("day") };
    }
    // fallback final
    const m = ahoraChile();
    return { from: m.startOf("month"), to: m.endOf("month") };
  };

  // Mes (YYYY-MM) para los deep-links a /arriendos desde las cards por sector.
  // finanzas-empresa es mensual; si por defensa el filtro estuviera en rango,
  // usamos el mes del inicio del rango.
  const mesParaDeeplink = () => {
    const ff = filtroFecha;
    if (ff?.modo === "rango" && ff.rango) return ff.rango[0].format("YYYY-MM");
    return (ff?.mes ?? ahoraChile()).format("YYYY-MM");
  };

  // Lo que impone la URL se muestra pero NO se recuerda (`setFiltersSinRecordar`):
  // abrir una alerta de un arriendo puntual no puede dejar ese arriendo como el
  // filtro preferido del admin.
  useEffect(() => {
    const tab = searchParams.get("tab");

    // Filtro por SECTOR vía deep-link (?sectorId=) desde las cards de
    // /finanzas-empresa. Solo aplica en /arriendos (la vista operativa); en la
    // vista de reporte (showKpiBySector) las cards ya agrupan por sector.
    if (!showKpiBySector) {
      const sidRaw = searchParams.get("sectorId");
      const sid = sidRaw ? Number(sidRaw) : null;
      if (sid && Number.isFinite(sid)) {
        setFiltersSinRecordar((prev) =>
          prev?.idSector === sid ? prev : { ...prev, idSector: sid },
        );
        setPage(1);
      }
    }

    // Prefiltro por arriendo puntual: el botón "Ver en finanzas" del
    // calendario navega a /arriendos?bookingId=123 para mostrar SOLO ese
    // arriendo. Tiene prioridad sobre el filtro por propiedad. Se aplica
    // siempre que venga el param (solo lo emite ese flujo).
    const bidRaw = searchParams.get("bookingId");
    const bid = bidRaw ? Number(bidRaw) : null;
    if (bid && Number.isFinite(bid)) {
      setFiltersSinRecordar((prev) => {
        if (prev?.bookingId === bid) return prev;
        // Partimos de CERO, no de los filtros que hubiera: un arriendo puntual
        // cruzado con un "Plataforma = Booking" recordado se resuelve en AND y
        // deja la tabla vacía bajo el cartel "Mostrando un solo arriendo".
        return { bookingId: bid };
      });
      setPage(1);
      return;
    }

    // Filtro por propiedad: en /finanzas-empresa llega con `tab=arriendos`;
    // en /arriendos no hay `tab` (la sección siempre es la de arriendos).
    if (tab !== null && tab !== "arriendos") return;

    const pidRaw = searchParams.get("propiedadId");
    const pid = pidRaw ? Number(pidRaw) : null;
    if (!pid || !Number.isFinite(pid)) return;

    // Evita setear si ya está
    setFiltersSinRecordar((prev) => {
      if (prev?.propiedadId === pid) return prev;
      return { ...prev, propiedadId: pid };
    });
    setPage(1);
  }, [searchParams, showKpiBySector, setFiltersSinRecordar]);

  const fetchArriendos = useCallback(
    async (
      uiFilters: Record<string, any> = {},
      newPage = 1,
      // El tamaño viaja como argumento y NO se lee del estado: cuando el
      // usuario cambia el selector, el `setSize` del mismo tick todavía no se
      // reflejó acá, así que leerlo del estado pediría la página con el tamaño
      // viejo. Es el mismo motivo por el que `newPage` también es argumento.
      newSize = tamanoPagina,
    ) => {
      try {
        setLoading(true);
        const globalRange = buildGlobalRange();
        const globalDateRange = globalRange
          ? [
              globalRange.from.format("YYYY-MM-DD"),
              globalRange.to.format("YYYY-MM-DD"),
            ]
          : undefined;

        const body = {
          ...mapUiToApi(uiFilters, newPage, newSize),
          globalDateRange,
          page: newPage,
        };
        body.page = newPage;
        const json = await finanzasEmpresaApi.searchArriendos(body);
        if (!json) return;
        const reservas = json.data.map((r: any) => ({
          ...r,
          extrasComisionClp: normalizeJsonb(r.extrascomisionclp),
          extrasComisionUsd: normalizeJsonb(r.extrascomisionusd),
          extrasSinComisionClp: normalizeJsonb(r.extrassincomisionclp),
          extrasSinComisionUsd: normalizeJsonb(r.extrassincomisionusd),
        }));
        setData(reservas);
        setTotal(json.total);
      } catch (err: any) {
        getMsg()?.error(err.message || "Error al cargar arriendos");
      } finally {
        setLoading(false);
      }
    },
    [page, tamanoPagina, filtroFecha],
  );

  useEffect(() => {
    // En modo reporte (showKpiBySector) la tabla paginada está oculta —
    // saltamos su fetch para no quemar requests al backend. La data del
    // resumen viene por la pipeline separada `fetchAllForKpi`.
    if (showKpiBySector) return;

    if (!filtroFecha) {
      setPage(1);
      fetchArriendos(filters, 1);
      return;
    }

    const fechaFiltro = (() => {
      if (filtroFecha.modo === "mes" || filtroFecha.modo === "anterior") {
        const m = filtroFecha.mes!;
        return {
          ingreso: [m.startOf("month").toDate(), m.endOf("month").toDate()],
        };
      }
      if (filtroFecha.modo === "rango") {
        return {
          ingreso: [
            filtroFecha.rango![0].toDate(),
            filtroFecha.rango![1].toDate(),
          ],
        };
      }
    })();

    const filtrosActualizados = {
      ...filters,
      ...fechaFiltro,
    };
    setPage(1);
    fetchArriendos(filtrosActualizados, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroFecha, filters, refreshKey]);

  // Fetch de TODAS las filas del período para alimentar las KPI cards por
  // sector. Solo corre cuando `showKpiBySector` está activado — en
  // /arriendos no se quema esta llamada. Itera páginas con `fetchAllPages`
  // (mismo helper que usa la exportación XLS). Si el período tiene muchos
  // arriendos esto puede demorar un poco más que cargar la primera página,
  // pero es el costo de los agregados precisos.
  const fetchAllForKpi = useCallback(
    async (uiFilters: Record<string, any> = {}) => {
      if (!showKpiBySector) return;
      try {
        setLoadingKpi(true);
        const globalRange = buildGlobalRange();
        const globalDateRange = globalRange
          ? [
              globalRange.from.format("YYYY-MM-DD"),
              globalRange.to.format("YYYY-MM-DD"),
            ]
          : undefined;
        const rows = await fetchAllPages<ArriendoRegistro>(async (pageN, pageSize) => {
          const body = {
            ...mapUiToApi(uiFilters, pageN),
            globalDateRange,
            page: pageN,
            page_size: pageSize,
          };
          const json = await finanzasEmpresaApi.searchArriendos(body);
          if (!json) return null;
          return { data: json.data as ArriendoRegistro[], total: json.total };
        });
        setAllRowsForKpi(rows);
      } catch (err: any) {
        getMsg()?.error(err?.message || "Error al cargar KPIs por sector");
      } finally {
        setLoadingKpi(false);
      }
    },
    // `mapUiToApi` y `buildGlobalRange` son closures estables sobre filtroFecha;
    // las dependencias relevantes son el flag y filtroFecha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showKpiBySector, filtroFecha],
  );

  // Re-fetch all-rows-for-KPI cuando cambia el filtro de fecha o los filtros.
  useEffect(() => {
    if (!showKpiBySector) return;
    fetchAllForKpi(filters);
  }, [showKpiBySector, filters, filtroFecha, fetchAllForKpi, refreshKey]);

  // Carga inicial del catálogo de propiedades — necesario para el
  // denominador de ocupación por sector. Pedimos hasta 1000 con un solo
  // round-trip (en práctica AF tiene <500 props). El endpoint admin
  // devuelve TODAS, incluidas las no habilitadas — filtramos
  // client-side. Si en el futuro crece, paginar con `fetchAllPages`
  // adaptado.
  useEffect(() => {
    if (!showKpiBySector) return;
    let cancelled = false;
    (async () => {
      const res = await propiedadesApi.getAll({ limit: 1000 });
      if (cancelled || !res) return;
      const enabled = (res.data ?? []).filter((p) => p.habilitado !== false);
      setPropsCatalog(enabled);
    })();
    return () => {
      cancelled = true;
    };
  }, [showKpiBySector]);

  // Catálogo de sectores para el Select "Sector" del toolbar (solo /arriendos).
  useEffect(() => {
    if (showKpiBySector) return;
    let cancelled = false;
    (async () => {
      const json = await propiedadesApi.getSectores();
      if (cancelled) return;
      const rows = (json?.data ?? []) as Array<{ id: number; sector: string }>;
      setSectorOpts(
        rows
          .filter((s) => s?.id != null && s?.sector)
          .map((s) => ({ id: s.id, sector: s.sector })),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [showKpiBySector]);

  // Dispara una búsqueda rápida por texto libre. Mantiene los filtros
  // estructurados existentes y agrega `_q` con el término. Resetea
  // paginación a 1 — los resultados pueden cambiar de tamaño.
  const handleQuickSearch = (value: string) => {
    const q = value.trim();
    const next: Record<string, any> = { ...filters };
    if (q) next._q = q;
    else delete next._q;
    setFilters(next);
    setPage(1);
    fetchArriendos(next, 1);
  };

  // Quita el prefiltro por arriendo puntual (llegado desde el botón "Ver
  // en finanzas" del calendario, /arriendos?bookingId=...). Vuelve a
  // mostrar todos los arriendos del período. También limpia el query param
  // de la URL para que un refresh no lo vuelva a aplicar.
  const clearBookingFilter = () => {
    const { bookingId: _omit, ...rest } = filters;
    setFilters(rest);
    setPage(1);
    fetchArriendos(rest, 1);
    if (searchParams.has("bookingId")) {
      const sp = new URLSearchParams(searchParams);
      sp.delete("bookingId");
      setSearchParams(sp, { replace: true });
    }
  };

  // ── Agregación por sector (alimenta las KPI cards) ───────────────────
  //
  // Recorremos `allRowsForKpi` y agrupamos por `sector` (nombre). Las
  // filas sin sector caen en el grupo "Sin sector". Por cada grupo
  // calculamos:
  //   • count           = cantidad de arriendos en el período
  //   • ingresoAfTotal  = Σ Ganancia AF Empresa (lo que entra a AF)
  //   • reservaTotal    = Σ lo que paga el huésped (gross)
  //   • ingresoCliente  = Σ Ingreso Cliente (lo que va al propietario)
  //   • nochesTotal     = Σ noches (para promedio de estadía)
  //   • precioNochesTotal = Σ precio noches (para precio/noche prom.)
  //   • avgEstadia      = nochesTotal / count
  //   • avgPrecioNoche  = precioNochesTotal / nochesTotal
  //   • avgGananciaAf   = ingresoAfTotal / count

  // Construir mapa sectorId → { nombre, total } a partir del catálogo
  // habilitado. Esto da el DENOMINADOR de la ocupación por sector. El
  // grupo "Sin sector" no aparece acá (no es un sector real) — su
  // denominador queda en 0 y la card lo muestra como "sin catálogo".
  const propsPorSector = useMemo(() => {
    const map = new Map<
      number,
      { sectorNombre: string; total: number; ids: Set<number> }
    >();
    for (const p of propsCatalog) {
      const sid = p.sector;
      if (sid == null) continue;
      let entry = map.get(sid);
      if (!entry) {
        entry = {
          sectorNombre: p.sectorNombre ?? "",
          total: 0,
          ids: new Set<number>(),
        };
        map.set(sid, entry);
      }
      entry.total += 1;
      entry.ids.add(p.id);
    }
    return map;
  }, [propsCatalog]);

  // Mapa propiedad → sector (id), derivado del catálogo habilitado. Sirve para
  // agregar los KPIs de ocupación por sector. Propiedades sin sector → null.
  const propIdToSectorId = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const p of propsCatalog) m.set(p.id, p.sector ?? null);
    return m;
  }, [propsCatalog]);

  // Ocupación noche-por-noche agregada por sector (id → {ocupadas, disponibles})
  // y global. Vienen del endpoint /estadisticas/ocupacion-kpis (igual que el
  // cron). "__sin__" junta las propiedades sin sector. Alimentan la métrica
  // "Días ocupados" de las cards como razón agrupada Σocupadas/Σdisponibles
  // (P4, D2). Independiente de los filtros de la tabla.
  const ocupacionPorSector = useMemo(() => {
    const map = new Map<number | "__sin__", { ocupadas: number; disponibles: number }>();
    for (const [propId, k] of ocupacionKpis) {
      const sid = propIdToSectorId.get(propId);
      const key = sid == null ? "__sin__" : sid;
      let e = map.get(key);
      if (!e) {
        e = { ocupadas: 0, disponibles: 0 };
        map.set(key, e);
      }
      e.ocupadas += k.nochesOcupadas;
      e.disponibles += k.nochesDisponibles;
    }
    return map;
  }, [ocupacionKpis, propIdToSectorId]);

  const ocupacionGlobal = useMemo(() => {
    let ocupadas = 0;
    let disponibles = 0;
    for (const [, k] of ocupacionKpis) {
      ocupadas += k.nochesOcupadas;
      disponibles += k.nochesDisponibles;
    }
    return { ocupadas, disponibles };
  }, [ocupacionKpis]);

  const sectorAggregates = useMemo(() => {
    // Trackeamos arriendos + propiedades distintas por sector. El SetU se
    // usa solo durante la pasada — al final lo dejamos fuera del objeto
    // exportado (mantenemos solo el count para no inflar memoria).
    interface SectorKpiInternal extends SectorKpiAgg {
      _propsArrendadasSet: Set<number>;
    }
    const map = new Map<string, SectorKpiInternal>();
    const SIN_SECTOR_KEY = "__sin_sector__";
    for (const r of allRowsForKpi) {
      const nombre = r.sector ?? null;
      const key = nombre ?? SIN_SECTOR_KEY;
      let agg = map.get(key);
      if (!agg) {
        const sid = r.idSector ?? null;
        const fromCatalog = sid != null ? propsPorSector.get(sid) : undefined;
        agg = {
          sectorId: sid,
          sectorNombre: nombre ?? "Sin sector",
          esSinSector: nombre == null,
          count: 0,
          ingresoAfTotal: 0,
          reservaTotal: 0,
          ingresoCliente: 0,
          nochesTotal: 0,
          precioNochesTotal: 0,
          propsDisponibles: fromCatalog?.total ?? 0,
          propsArrendadas: 0,
          nochesOcupadasKpi: 0,
          nochesDisponiblesKpi: 0,
          _propsArrendadasSet: new Set<number>(),
        };
        map.set(key, agg);
      }
      agg.count += 1;
      agg.ingresoAfTotal += Number(r.ingresoafclp) || 0;
      agg.reservaTotal += Number(r.reservatotalclp) || 0;
      agg.ingresoCliente += Number(r.reservatotalpropietario) || 0;
      agg.nochesTotal += Number(r.noches) || 0;
      agg.precioNochesTotal += Number(r.precionochesclp) || 0;
      if (r.idPropiedad != null) {
        agg._propsArrendadasSet.add(r.idPropiedad);
      }
    }

    // Además: sectores del catálogo que NO tuvieron arriendos en el
    // período igual aparecen como cards (con 0 arriendos). Útil porque
    // un sector "vacío" es información financiera relevante — significa
    // que no se rentabilizó nada de esa zona.
    for (const [sid, info] of propsPorSector.entries()) {
      const nombre = info.sectorNombre || `Sector ${sid}`;
      if (map.has(nombre)) continue;
      map.set(nombre, {
        sectorId: sid,
        sectorNombre: nombre,
        esSinSector: false,
        count: 0,
        ingresoAfTotal: 0,
        reservaTotal: 0,
        ingresoCliente: 0,
        nochesTotal: 0,
        precioNochesTotal: 0,
        propsDisponibles: info.total,
        propsArrendadas: 0,
        nochesOcupadasKpi: 0,
        nochesDisponiblesKpi: 0,
        _propsArrendadasSet: new Set<number>(),
      });
    }

    // Materializar `propsArrendadas` + ocupación noche-por-noche, descartar el
    // Set interno.
    const result: SectorKpiAgg[] = Array.from(map.values()).map((a) => {
      // Ocupación del sector desde los KPIs noche-por-noche (P4, D2).
      const occ =
        a.sectorId == null
          ? ocupacionPorSector.get("__sin__")
          : ocupacionPorSector.get(a.sectorId);
      return {
        sectorId: a.sectorId,
        sectorNombre: a.sectorNombre,
        esSinSector: a.esSinSector,
        count: a.count,
        ingresoAfTotal: a.ingresoAfTotal,
        reservaTotal: a.reservaTotal,
        ingresoCliente: a.ingresoCliente,
        nochesTotal: a.nochesTotal,
        precioNochesTotal: a.precioNochesTotal,
        propsDisponibles: a.propsDisponibles,
        propsArrendadas: a._propsArrendadasSet.size,
        nochesOcupadasKpi: occ?.ocupadas ?? 0,
        nochesDisponiblesKpi: occ?.disponibles ?? 0,
      };
    });
    // Orden descendente por Ganancia AF (lo importante para finanzas según
    // la consigna). "Sin sector" se ordena igual que el resto — si tiene
    // ganancia significativa, queremos verlo arriba.
    result.sort((a, b) => b.ingresoAfTotal - a.ingresoAfTotal);
    return result;
  }, [allRowsForKpi, propsPorSector, ocupacionPorSector]);

  // Totales globales del período (para la card-encabezado). Suma todo lo
  // de `sectorAggregates` excepto `propsDisponibles`, que se toma del
  // catálogo entero (no de la suma por sector — eso doble-contaría el
  // grupo "Sin sector" o ignoraría sectores sin arriendos).
  const totalesGlobales = useMemo<TotalesAgg>(() => {
    const base = sectorAggregates.reduce(
      (acc, a) => {
        acc.count += a.count;
        acc.ingresoAfTotal += a.ingresoAfTotal;
        acc.reservaTotal += a.reservaTotal;
        acc.ingresoCliente += a.ingresoCliente;
        acc.nochesTotal += a.nochesTotal;
        acc.precioNochesTotal += a.precioNochesTotal;
        acc.propsArrendadas += a.propsArrendadas;
        return acc;
      },
      {
        count: 0,
        ingresoAfTotal: 0,
        reservaTotal: 0,
        ingresoCliente: 0,
        nochesTotal: 0,
        precioNochesTotal: 0,
        propsDisponibles: propsCatalog.length,
        propsArrendadas: 0,
        nochesOcupadasKpi: 0,
        nochesDisponiblesKpi: 0,
      } as TotalesAgg,
    );
    // Ocupación global = suma de TODAS las propiedades habilitadas con KPI (no
    // la suma de los sectores: eso podría dejar fuera props sin sector que no
    // tuvieron arriendos). P4, D2.
    base.nochesOcupadasKpi = ocupacionGlobal.ocupadas;
    base.nochesDisponiblesKpi = ocupacionGlobal.disponibles;
    return base;
  }, [sectorAggregates, propsCatalog.length, ocupacionGlobal]);

  // Rango [from, to] (YYYY-MM-DD, inclusivo) del período activo — se usa para
  // pedir los KPIs de ocupación al backend. Para modo `mes`/`anterior` es el
  // mes completo; para `rango`, las dos fechas elegidas. (P4: reemplazó al
  // viejo `periodDays`, que ya no se usa — la ocupación se calcula noche-por-
  // noche en el backend, no con días × propiedades.)
  const periodRange = useMemo<{ from: string; to: string }>(() => {
    const ff = filtroFecha;
    const FMT = "YYYY-MM-DD";
    if (ff?.modo === "rango" && ff.rango) {
      return { from: ff.rango[0].format(FMT), to: ff.rango[1].format(FMT) };
    }
    const m = ff?.mes ?? ahoraChile();
    return { from: m.startOf("month").format(FMT), to: m.endOf("month").format(FMT) };
  }, [filtroFecha]);

  // Carga los KPIs de ocupación del período activo (noche-por-noche, backend).
  // Independiente de los filtros de la tabla: el denominador es el catálogo
  // habilitado completo, igual que `propsDisponibles`.
  useEffect(() => {
    if (!showKpiBySector) return;
    let cancelled = false;
    (async () => {
      const kpis = await estadisticasApi.getOcupacionKpis(
        periodRange.from,
        periodRange.to,
      );
      if (cancelled) return;
      setOcupacionKpis(new Map(kpis.map((k) => [k.idPropiedad, k])));
    })();
    return () => {
      cancelled = true;
    };
  }, [showKpiBySector, periodRange, refreshKey]);

  // Etiqueta del rango activo (para el badge "Período").
  const rangoActivoLabel = useMemo(() => {
    const ff = filtroFecha;
    if (ff?.modo === "todos") {
      return "Todos los arriendos (sin filtro de fecha)";
    }
    if (!ff || ff.modo === "mes") {
      const m = ff?.mes ?? ahoraChile();
      return m.format("MMMM YYYY");
    }
    if (ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile().subtract(1, "month");
      return `${m.format("MMMM YYYY")} (mes anterior)`;
    }
    if (ff.modo === "rango" && ff.rango) {
      return `${ff.rango[0].format("DD-MM-YYYY")} → ${ff.rango[1].format("DD-MM-YYYY")}`;
    }
    return "Mes actual";
  }, [filtroFecha]);

  return (
    <div className="finanzas-section">
      {/*
        Bloque de KPI cards por sector. Solo se renderiza cuando el
        componente se monta en /finanzas-empresa (showKpiBySector=true).
        En /arriendos queda oculto — esa vista es operativa, las cards
        viven en la pestaña de reporte.
      */}
      {showKpiBySector && (
        <KpiBySectorBlock
          sectorAggregates={sectorAggregates}
          totales={totalesGlobales}
          loading={loadingKpi}
          rangoLabel={rangoActivoLabel}
          onVerSector={(sectorId) => {
            // Deep-link a /arriendos con el SECTOR y el MES activos. /arriendos
            // lee `sectorId` (filtro por sector real, resuelto server-side a
            // las propiedades del sector) y `mes` (aterriza en ese mes). Antes
            // solo pasaba `sectorId`, que /arriendos ignoraba, y sin mes → por
            // eso "filtraba pésimo".
            const mes = mesParaDeeplink();
            if (sectorId != null) {
              navigate(`/arriendos?sectorId=${sectorId}&mes=${mes}`);
            } else {
              navigate(`/arriendos?mes=${mes}`);
            }
          }}
          onVerTodos={() => navigate(`/arriendos?mes=${mesParaDeeplink()}`)}
        />
      )}

      {/*
        Toolbar + tabla solo se muestran en modo operativo (/arriendos).
        En /finanzas-empresa (showKpiBySector=true) la vista es puro
        reporte agregado: cards arriba + total del período. Para
        operaciones por arriendo (filtrar, buscar, exportar, publicar
        bulk al cliente) el banner ya invita "Ir a Arriendos".
      */}
      {!showKpiBySector && (
      <div className="toolbar" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {/*
          Buscador rápido. Llega al backend como `_q` (search server-side
          que resuelve el handler `arriendosEmpresa` con OR iLike).
          Para filtros estructurados (rango de fechas, plataforma, monto,
          etc.) usar "Filtro avanzado" a la derecha.
        */}
        <Input.Search
          allowClear
          placeholder="Buscar por código de estadía (#1472), de plataforma (MJ23J), huésped, propiedad, nota…"
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          suffix={
            <Tooltip
              title={
                <span>
                  Busca por cualquier código o dato del arriendo:
                  <br />• <b>Código de estadía AF</b> interno (#1472)
                  <br />• <b>Código de plataforma</b> Airbnb/Booking (ej. MJ23J
                  = «ID Reserva C.»)
                  <br />• <b>ID de Stays</b> y <b>número corto</b> manual (AF-…)
                  <br />• <b>Huésped</b>, <b>propiedad</b>, teléfono o nota
                </span>
              }
            >
              <InfoCircleOutlined style={{ color: "#bfbfbf" }} />
            </Tooltip>
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onSearch={handleQuickSearch}
          enterButton
          style={{ maxWidth: 460 }}
        />
        {/* Filtro por SECTOR (server-side vía `_idSector` → propiedades del
            sector). Se preselecciona con el deep-link ?sectorId= desde las
            cards de /finanzas-empresa. Al cambiar, el efecto [filtroFecha,
            filters] refetch automáticamente. */}
        <Select
          allowClear
          showSearch
          placeholder="Sector"
          optionFilterProp="label"
          value={filters.idSector ?? undefined}
          onChange={(val) => {
            setFilters((prev) => {
              const next = { ...prev };
              if (val == null) delete next.idSector;
              else next.idSector = val;
              return next;
            });
            setPage(1);
          }}
          options={sectorOpts.map((s) => ({ value: s.id, label: s.sector }))}
          style={{ minWidth: 180 }}
        />
        <Filtros
          persistenceKey={"arriendos"}
          // Sin `value` el filtro recordado quedaría invisible: la tabla sale
          // recortada y el panel, vacío.
          value={filters}
          items={[
            {
              key: "codigoEstadia",
              type: "text",
              label: "Código de estadía (#)",
            },
            { key: "idReservaC", type: "text", label: "ID Reserva C" },
            {
              key: "propiedadId",
              type: "select",
              label: "Propiedad",
              mode: "multiple",
              options: propiedades.map((p) => ({
                value: p.id,
                label: formatPropertyName(p.nombre),
              })),
            },
            { key: "nombreHuesped", type: "text", label: "Huésped" },
            { key: "telefonoHuesped", type: "text", label: "Teléfono" },
            { key: "salida", type: "dateRange", label: "Salida" },
            {
              key: "nombrePlataforma",
              type: "select",
              label: "Plataforma",
              mode: "multiple",
              options: [
                { value: "AirBnb", label: "AirBnb" },
                { value: "Booking", label: "Booking" },
                { value: "Despegar", label: "Despegar" },
                { value: "Stays", label: "Web" },
                { value: "Directa", label: "Directo" },
              ],
            },
            { key: "notaInterna", type: "text", label: "Nota interna" },
          ]}
          optionalItems={[
            {
              key: "salidaTardia",
              type: "select",
              label: "Salida tardía",
              options: [
                { value: true, label: "Sí" },
                { value: false, label: "No" },
              ],
            },
            {
              key: "reservaTotalClp",
              type: "numberRange",
              label: "Reserva total (CLP)",
            },
            {
              key: "limpiezaClp",
              type: "numberRange",
              label: "Limpieza (CLP)",
            },
            {
              key: "comisionPlataformaClp",
              type: "numberRange",
              label: "Comisión plataforma (CLP)",
            },
            // Los 4 numberRange de "Extras" se retiraron (auditoría
            // 2026-07-13): extras_comision_clp/usd y extras_sin_comision_*
            // son columnas JSONB (listas de extras), un rango numérico no
            // aplica y el filtro era un no-op con label duplicado.
            {
              key: "reservaTotalPropietario",
              type: "numberRange",
              label: "Ingresos (CLP)",
            },
          ]}
          onApply={(appliedFilters) => {
            // Preserva la búsqueda rápida `_q` y el filtro de SECTOR: el panel
            // de filtros no los conoce y antes los borraba al aplicar, dejando
            // resultados que no correspondían al texto/sector aún visible.
            const merged = { ...appliedFilters };
            if (filters._q) merged._q = filters._q;
            if (filters.idSector != null) merged.idSector = filters.idSector;
            setFilters(merged);
            setPage(1);
            fetchArriendos(merged, 1);
          }}
        />
        <Button
          onClick={async () => {
            // Round 2026-05-08: exportamos TODAS las páginas, no solo la
            // visible. Antes pasábamos `rows: data` que es la página
            // actual del state — el XLS quedaba con 20-50 filas
            // dependiendo del page_size. Ahora iteramos páginas con los
            // filtros activos via fetchAllPages.
            const hide = getMsg()?.loading("Preparando exportación...", 0);
            try {
              const globalRange = buildGlobalRange();
              const globalDateRange = globalRange
                ? [
                    globalRange.from.format("YYYY-MM-DD"),
                    globalRange.to.format("YYYY-MM-DD"),
                  ]
                : undefined;
              const allRows = await fetchAllPages<any>(async (page, pageSize) => {
                const body = {
                  ...mapUiToApi(filters, page),
                  globalDateRange,
                  page,
                  page_size: pageSize,
                };
                const json = await finanzasEmpresaApi.searchArriendos(body);
                if (!json) return null;
                const reservas = json.data.map((r: any) => ({
                  ...r,
                  extrasComisionClp: normalizeJsonb(r.extrascomisionclp),
                  extrasComisionUsd: normalizeJsonb(r.extrascomisionusd),
                  extrasSinComisionClp: normalizeJsonb(r.extrassincomisionclp),
                  extrasSinComisionUsd: normalizeJsonb(r.extrassincomisionusd),
                }));
                return { data: reservas, total: json.total };
              });
              const now = ahoraChile().format("YYYY-MM-DD_HH-mm");
              await exportToXlsxSingleSheet({
                filename: `Arriendos_${now}.xlsx`,
                sheetName: "Arriendos",
                rows: allRows.map((r: any) => ({
                  ...r,
                  telefonohuesped: formatPhoneDisplay(r.telefonohuesped),
                })),
              });
              getMsg()?.success(`Exportadas ${allRows.length} filas`);
            } catch (err: any) {
              getMsg()?.error(err?.message || "Error al exportar");
            } finally {
              hide?.();
            }
          }}
        >
          Exportar a XLS
        </Button>

        {/* El control de "Publicar / cerrar mes al cliente" se movió a una
            tarjeta destacada arriba de /finanzas-empresa (CierreMensualCard):
            publica el MES COMPLETO (todos los flujos), no solo arriendos, así
            que no correspondía enterrarlo en la barra de /arriendos. Round
            2026-07-17. Ver docs/flujo-financiero.md §13. */}
      </div>
      )}

      {/*
        Banner del prefiltro por arriendo puntual: aparece cuando se llegó
        desde el botón "Ver en finanzas" del calendario
        (/arriendos?bookingId=...). Hace evidente por qué la tabla muestra
        un solo arriendo y ofrece quitar el filtro para ver todos.
      */}
      {!showKpiBySector && filters.bookingId != null && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Mostrando un solo arriendo (abierto desde el calendario)."
          action={
            <Button size="small" onClick={clearBookingFilter}>
              Quitar filtro
            </Button>
          }
        />
      )}

      {!showKpiBySector && (
      <Table
        // Clase para anular el `overflow-x: auto` global del
        // `.ant-table-wrapper` (Css/shared-page.css). Ese overflow crea un
        // contenedor de scroll que atrapa el `position: sticky` del header
        // y hace que NO siga el scroll de la página. Ver
        // `.arriendos-sticky-table` en finanzasEmpresa.css.
        className="arriendos-sticky-table"
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={buildColumns()}
        pagination={paginacionServidor({
          page,
          size: tamanoPagina,
          total,
          etiqueta: "arriendo",
          onChange: (p, s) => {
            // Los DOS argumentos. Al cambiar el tamaño, AntD manda también la
            // página recalculada; ignorar `s` dejaba el selector muerto.
            const cambioTamano = s !== tamanoPagina;
            const paginaFinal = cambioTamano ? 1 : p;
            setTamanoPagina(s);
            setPage(paginaFinal);
            fetchArriendos(filters, paginaFinal, s);
          },
        })}
        scroll={{ x: 1300 }}
        // Header sticky: al scrollear la página, la fila de títulos de
        // columna queda fija para no perder de vista a qué corresponde
        // cada valor. `offsetHeader: 64` la posiciona justo debajo del
        // header de la app (`.home-header`, sticky con height 64px) en
        // vez de tapada por él.
        sticky={{ offsetHeader: 64 }}
      />
      )}
    </div>
  );
}
